import { initSmartService } from '@pypestream/design-system';
import { init as initI18n, LanguageScopes } from '@pypestream/translations';
import {
  UNKNOWN_ENVIRONMENT,
  initSentryWithLaunchDarklyGate,
  isDevelopment,
  isProduction,
  reportError,
  showGitInfo,
} from '@pypestream/utils';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import 'requestidlecallback-polyfill';

import App from './app';
import { ldClient } from './xstate/app.xstate';
import { reloadOnceForChunkError } from './components/error-boundaries/helpers';
import { getAnalytics } from './hooks/use-analytics';
import { localBackendEnvironment } from './utils/envs-related-data';
import './index.scss';

window.addEventListener('vite:preloadError', (event: Event) => {
  // `preventDefault` is what makes this invisible: it stops the failure from
  // becoming an unhandled rejection, which is the ONLY route by which Sentry's
  // global handler would have seen it. So the report has to be raised here, or
  // a stale-chunk storm after a deploy shows up as nothing but users reloading.
  // `reloaded` says whether the guard let a second reload happen, which is the
  // difference between a recoverable blip and the crash page.
  const reloaded = reloadOnceForChunkError();

  try {
    reportError(
      (event as Event & { payload?: unknown }).payload ??
        new Error('vite:preloadError'),
      {
        source: 'vite-preload-error',
        tags: { app: 'pro-studio' },
        extra: { reloaded },
      }
    );
  } catch {
    // The reload above is the recovery; telemetry must not get in its way.
  }

  event.preventDefault();
});

// Logged unconditionally so PSTUDIO-2406 perf bench users can confirm what
// Vite saw at startup — set FE_PERF_HARNESS=1 before `yarn start`/`yarn build`
// to enable. If this prints `undefined`, the env var didn't reach Vite.
console.info(
  '[perf-harness] FE_PERF_HARNESS =',
  import.meta.env.FE_PERF_HARNESS
);
if (import.meta.env.FE_PERF_HARNESS === '1') {
  import('./dev/perf-harness');
}

// Project Bobcat console helpers (`__bobcat__.status/reset/regroup`). Non-prod
// only, same shape as the perf harness — the auto-grouping is one-shot per
// solution, so re-testing it needs a way to clear the groups it created.
if (!isProduction) {
  import('./dev/bobcat-harness');
}

showGitInfo({
  appName: 'Pro-Studio',
  commitHash: process.env.GIT_COMMIT_HASH,
  branchName: process.env.GIT_BRANCH_NAME,
  tagName: process.env.GIT_TAG_NAME,
  version: process.env.VITE_APP_VERSION,
});

// On a local dev server, the header's tool links (Contact Center, Analytics,
// Manager) go to the environment this app's backend is on, not always main —
// read by the design system's `generateBaseURLsToTools` (LOCAL_BACKEND_ENV_KEY).
(globalThis as Record<string, unknown>).__PS_LOCAL_BACKEND_ENV__ =
  localBackendEnvironment();

const analytics = getAnalytics();
if (analytics) {
  if (typeof analytics.init === 'function') {
    analytics.init(import.meta.env.FE_POSTHOG_API_KEY, {
      api_host: import.meta.env.FE_POSTHOG_API_HOST,
      persistence: 'localStorage',
      opt_out_capturing_by_default: true,
      disable_session_recording: true,
      session_recording: {
        recordCrossOriginIframes: true,
        maskAllInputs: false,
        maskTextSelector: '[data-private]',
      },
      capture_pageview: true,
      capture_pageleave: true,
      // Pinned explicitly rather than left to the default. posthog-js 1.69 had
      // no such option and behaved as 'always'; from the version this repo now
      // pins, the default is 'identified_only', which stops creating person
      // profiles for anonymous users. Stating it keeps the analytics behaviour
      // identical across the upgrade instead of changing it as a side effect.
      person_profiles: 'always',
    });
  }

  if (typeof analytics.register === 'function') {
    analytics.register({
      'Pypestream Product': 'Pro-studio',
      // Same fallback Sentry uses, so the two tools never disagree about which
      // deploy an event came from — a missing value must look missing in BOTH.
      'Pypestream Environment':
        import.meta.env.ANALYTICS_CAPTURE_ENVIRONMENT || UNKNOWN_ENVIRONMENT,
      'Git Commit': import.meta.env.ANALYTICS_CAPTURE_GIT_COMMIT,
      'Git Tags': import.meta.env.ANALYTICS_CAPTURE_GIT_TAGS
        ? import.meta.env.ANALYTICS_CAPTURE_GIT_TAGS.split(',')
        : null,
      'GitHub PR ID': import.meta.env.ANALYTICS_CAPTURE_GITHUB_PR_ID,
      'GitHub PR URL': import.meta.env.ANALYTICS_CAPTURE_GITHUB_PR_URL,
    });
  }
}
let container: HTMLElement | null = null;

document.addEventListener('DOMContentLoaded', async function () {
  if (!container) {
    container = document.getElementById('root') as HTMLElement;
    const root = createRoot(container);

    initSmartService({
      endpoints: {
        gql: import.meta.env.FE_GQL_ENDPOINT,
        wss: import.meta.env.FE_GQL_WSS_ENDPOINT,
        kratos: import.meta.env.FE_KRATOS_ENDPOINT,
      },
      launchDarklyKey: import.meta.env.FE_LAUNCH_DARKLY_API_KEY,
    });

    await initI18n(LanguageScopes.proStudio);

    const proStudioSentryDSN = import.meta.env
      .FE_PRO_STUDIO_SENTRY_DSN as string;

    // Which deploys report is decided in LaunchDarkly, not by a list of
    // environment names in the source. `ANALYTICS_CAPTURE_ENVIRONMENT` is still
    // passed, but only as the event's tag.
    //
    // `isDevelopment` stays, and it is not redundant with the flag: local dev
    // builds read the `next` LaunchDarkly environment — the same one the `main`
    // deploy reads, since both take their client-side id from the same key — so
    // the flag cannot tell a developer's laptop from the deployed `main`. This
    // is the only thing that can.
    if (!isDevelopment && proStudioSentryDSN) {
      initSentryWithLaunchDarklyGate(
        proStudioSentryDSN,
        { environment: import.meta.env.ANALYTICS_CAPTURE_ENVIRONMENT },
        { client: ldClient, flagKey: 'ps-sentry-enabled' }
      );
    }

    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  }
});
